/* eslint-disable array-callback-return */
// -- == //
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import * as S from './styles';
import Empty from 'components/empty';
import Skeleton from 'components/skeleton';

interface IProps {
  altanfeethiFields: any;
  headers: IPropsHeader[];
  data: any[];
  style?: object;
  totlaItems?: number;
  pagination?: boolean;
  headerStyle?: 'two' | 'one';
  headerColor?: string;
  loading?: boolean;
  /**
   * Shown instead of the table body when `data` is empty and nothing is
   * loading. Defaults to a generic `Empty`; pass a screen-specific one, or
   * `null` to keep the old bare-headers behaviour.
   */
  empty?: ReactNode;
}

interface IPropsHeader {
  id: string | number;
  title: string;
  icon?: string;
  dir?: 'left' | 'right';
  /**
   * Column width. The table lays out `fixed`, so this is the width the column
   * gets rather than a suggestion the content can overrule.
   */
  width?: string | number;
  padding?: string;
  /**
   * Name of the `altanfeethiFields` entry that fills this column. Without it,
   * cells are matched to headers by array position.
   */
  key?: string;
  /**
   * What the column holds. Drives alignment and cell typography:
   *
   *   num      right aligned, mono, tabular numerals
   *   actions  right aligned, always visible, quiet until hovered
   */
  type?: 'text' | 'num' | 'actions';
}

const alignOf = (header?: IPropsHeader): 'left' | 'right' | 'center' => {
  if (!header) return 'center';
  if (header.type === 'num' || header.type === 'actions') return 'right';
  return header.dir || 'center';
};

const classOf = (header?: IPropsHeader) => `align-${alignOf(header)}${header?.type ? ` ${header.type}` : ''}`;

/** Rows to draw while loading. A tree has no previous page to size against. */
const SKELETON_ROWS = 6;

/** How deep the branch flattener walks. Matches the six `level-N` classes. */
const MAX_DEPTH = 6;

/**
 * A body cell. Identical in behaviour to AppTable's: cells are one line tall
 * and clip with an ellipsis, and the full text goes onto `title` whenever the
 * cell is actually clipped, so nothing becomes silently unreadable.
 */
const Cell: FC<{ className: string; generation: number; children: ReactNode }> = ({
  className,
  generation,
  children
}) => {
  const ref = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    const cell = ref.current;
    if (!cell) return;

    const probe = (cell.firstElementChild as HTMLElement) || cell;
    const full = (cell.textContent || '').trim();

    if (full && probe.scrollWidth > probe.clientWidth + 1) cell.setAttribute('title', full);
    else cell.removeAttribute('title');
  }, [generation, children]);

  return (
    <S.Td ref={ref} className={className}>
      {children}
    </S.Td>
  );
};

const defaultEmpty = (
  <Empty title="No categories yet" text="Create a category and it appears here, with its sub-categories beneath it." />
);

const AppTreeTable: FC<IProps> = ({
  altanfeethiFields,
  data,
  headers,
  style,
  headerStyle = 'one',
  headerColor,
  loading,
  empty
}) => {
  let childLevel = 1;

  /**
   * Column widths move whenever the table does — the viewport, but also the
   * sidebar collapsing beside it — and with them what does and does not fit.
   */
  const container = useRef<HTMLDivElement>(null);
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!container.current || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setGeneration((n) => n + 1));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  const hasRows = Array.isArray(data) && data.length > 0;
  const emptyState = empty === undefined ? defaultEmpty : empty;
  const showEmpty = !loading && !hasRows && emptyState !== null;

  /**
   * One entry per rendered cell — the same contract AppTable uses. Keyed
   * tables follow the header order and look each cell up by name; the index
   * fallback reproduces the old positional behaviour exactly.
   */
  const fieldList: any[] = Object.values(altanfeethiFields || {});
  const keyed = Array.isArray(headers) && headers.some((header) => header?.key);
  const columns: { header?: IPropsHeader; render: any }[] = keyed
    ? headers.map((header, index) => ({
        header,
        render: (header.key && altanfeethiFields?.[header.key]) || fieldList[index]
      }))
    : fieldList.map((render, index) => ({ header: headers?.[index], render }));

  const renderChild = (item: any) => {
    if (item.children.length) {
      childLevel += 1;

      return item.children.map((itemChild: any, index: number) => (
        <>
          <S.Tr key={index} className={`${itemChild.level} parent-${item.id} hide`}>
            {columns.map((column, index2) => (
              <Cell key={`td-${index2}`} className={classOf(column.header)} generation={generation}>
                {typeof column.render === 'function' ? (
                  column.render(
                    itemChild,
                    true,
                    itemChild.children.length ? true : false,
                    () => {
                      [...(document.querySelectorAll(`.parent-${itemChild.id}`) as any)].map((el) => {
                        if (el.classList.contains('show')) {
                          el.classList.add('hide');
                          el.classList.remove('show');
                        } else if (el.classList.contains('hide')) {
                          el.classList.add('show');
                          el.classList.remove('hide');
                        }
                      });
                    },
                    `level-${childLevel}`
                  )
                ) : (
                  <></>
                )}
              </Cell>
            ))}
          </S.Tr>
          {renderChild(itemChild)}
        </>
      ));
    }
  };

  /**
   * Stamps `level-1` … `level-6` down the branch. The call site turns those
   * into the row's indent, so this is what makes the nesting visible.
   *
   * It replaces six hand-written copies of the same map. Depth is still
   * capped at six, exactly as before — a seventh level renders flat rather
   * than indented, which is the behaviour that already shipped.
   */
  const renderList = (list: any[], level = 1): any[] =>
    (list || []).map((item: any) => ({
      ...item,
      level: `level-${level}`,
      children: level < MAX_DEPTH ? renderList(item.children, level + 1) : item.children
    }));

  return (
    <>
      <S.TableCon ref={container}>
        {showEmpty ? (
          emptyState
        ) : (
          <S.Table style={style}>
            <S.Thead className={headerStyle}>
              <S.Tr>
                {headers?.map((item: IPropsHeader, index) => (
                  <S.Th
                    key={index}
                    className={classOf(item)}
                    style={{ width: item.width, padding: item.padding, color: headerColor }}
                  >
                    <S.HeaderTh
                      key={item.id}
                      style={{
                        justifyContent:
                          alignOf(item) === 'right' ? 'flex-end' : alignOf(item) === 'left' ? 'flex-start' : 'center'
                      }}
                    >
                      {item.icon ? (
                        <ReactSVG
                          src={item.icon}
                          beforeInjection={(svg) => {
                            svg.setAttribute('stroke', headerColor || 'currentColor');
                          }}
                        />
                      ) : (
                        <></>
                      )}
                      {item.title}
                    </S.HeaderTh>
                  </S.Th>
                ))}
              </S.Tr>
            </S.Thead>
            <S.Tbody>
              {loading
                ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
                    <S.Tr key={`skeleton-${index}`}>
                      {columns.map((column, index2) => (
                        <S.Td key={`skeleton-td-${index2}`} className={`align-${alignOf(column.header)}`}>
                          <Skeleton />
                        </S.Td>
                      ))}
                    </S.Tr>
                  ))
                : hasRows &&
                  renderList(data)?.map((item: any, index: number) => {
                    return (
                      <>
                        <S.Tr key={index} className={item.level}>
                          {columns.map((column, index2) => (
                            <Cell key={`td-${index2}`} className={classOf(column.header)} generation={generation}>
                              {typeof column.render === 'function' ? (
                                column.render(
                                  item,
                                  false,
                                  item.children.length ? true : false,
                                  (item: { id: any; children: any[] }) => {
                                    [...(document.querySelectorAll(`.parent-${item.id}`) as any)].map((el) => {
                                      if (el.classList.contains('show')) {
                                        el.classList.add('hide');
                                        el.classList.remove('show');
                                        if (item?.children?.length)
                                          item?.children?.map((item2) => {
                                            [...(document.querySelectorAll(`.parent-${item2.id}`) as any)].map((el) => {
                                              el.classList.add('hide');
                                              el.classList.remove('show');
                                              if (item?.children?.length)
                                                item?.children?.map((item2) => {
                                                  [...(document.querySelectorAll(`.parent-${item2.id}`) as any)].map(
                                                    (el) => {
                                                      el.classList.add('hide');
                                                      el.classList.remove('show');
                                                    }
                                                  );
                                                });
                                            });
                                          });
                                      } else if (el.classList.contains('hide')) {
                                        el.classList.add('show');
                                        el.classList.remove('hide');
                                      }
                                    });
                                  }
                                )
                              ) : (
                                <></>
                              )}
                            </Cell>
                          ))}
                        </S.Tr>

                        {renderChild(item)}
                      </>
                    );
                  })}
            </S.Tbody>
          </S.Table>
        )}
      </S.TableCon>
    </>
  );
};

export default AppTreeTable;
